import { ProctoringAssessmentConfigurationV2, ProctoringOption } from "@verity/proctoring-option/types";
import { EditQuizType, ExemptedStudent, ThirdPartyPlatform, VerityInstituteSettings } from "../types/common";
import { getPlatformIdFromUrl } from "@verity/url-matcher";
import { DEFAULT_PROCTORING_OPTION } from "@verity/proctoring-option";

export function hasProctoringOptionsChanged(
	initialProctoringOption?: Partial<ProctoringOption>,
	proctoringOption?: Partial<ProctoringOption>,
) {
	if (!initialProctoringOption || !proctoringOption) return false;
	const initialProctoringOptionEntries = Object.entries(initialProctoringOption).sort();
	const proctoringOptionEntries = Object.entries(proctoringOption).sort();

	for (let i = 0; i < initialProctoringOptionEntries.length; i++) {
		const [key, value] = initialProctoringOptionEntries[i];
		const currentValue = proctoringOptionEntries[i]?.[1];

		if (key === "showRecordingPanel") {
			//showRecordingPanel is set from false (default) to true without user input when they access the 'Method' tab
			continue;
		}

		if (key === "applicationBlockList") {
			if (proctoringOption.applicationBlockMode === "off") continue;
		} else if (key === "excludedWebTrafficRecordingUrls") {
			if (!proctoringOption.recordWebTraffic) continue;
		} else if (key === "keyboardShortcutBlockList") {
			if (!proctoringOption.recordInput) continue;
		}

		if (Array.isArray(value) && Array.isArray(currentValue)) {
			const sortedInitial = [...value].sort();
			const sortedCurrent = [...currentValue].sort();
			if (JSON.stringify(sortedInitial) !== JSON.stringify(sortedCurrent)) {
				return true;
			}
		} else if (value !== currentValue) {
			return true;
		}
	}
}

type AssessmentConfig = Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">;

export function hasAssessmentConfigurationChanged(
	initialProctoringAssessmentConfiguration?: AssessmentConfig,
	proctoringAssessmentConfiguration?: AssessmentConfig,
) {
	if (!initialProctoringAssessmentConfiguration || !proctoringAssessmentConfiguration) return false;

	const initialProctoringAssessmentConfigurationEntries = Object.entries(
		initialProctoringAssessmentConfiguration,
	).sort();
	const proctoringAssessmentConfigurationRefEntries = Object.entries(proctoringAssessmentConfiguration).sort();

	for (let i = 0; i < initialProctoringAssessmentConfigurationEntries.length; i++) {
		const [key, value] = initialProctoringAssessmentConfigurationEntries[i];
		const currentValue = proctoringAssessmentConfigurationRefEntries[i]?.[1];

		if (key === "password" || key === "enabled") {
			continue;
		}

		if (key === "thirdPartyAssessmentOption") {
			if (value && currentValue) {
				const initialThirdPartyEntries = Object.entries(value).sort();
				const thirdPartyEntries = Object.entries(currentValue).sort();

				for (let i = 0; i < initialThirdPartyEntries.length; i++) {
					const [thirdPartyKey, thirdPartyValue] = initialThirdPartyEntries[i];
					const thirdPartyCurrentValue = thirdPartyEntries[i]?.[1];

					if (thirdPartyKey === "password") {
						if (
							(thirdPartyValue === null || thirdPartyValue === "") &&
							(thirdPartyCurrentValue === null || thirdPartyCurrentValue === "")
						) {
							continue;
						}
					}

					if (thirdPartyValue !== thirdPartyCurrentValue) {
						return true;
					}
				}
			}
			continue;
		}

		if (value !== currentValue) {
			return true;
		}
	}
}

/**If any proctoring options are enabled */
export function areProctoringOptionsSet(
	proctoringOption?: Partial<ProctoringOption>,
	defaultProctoringOption?: ProctoringOption | null,
) {
	// Keys for DEFAULT_PROCTORING_OPTIONs that are not toggleable restrictions (isExtension, isLockdown, etc.)
	const proctoringOptionFilteredKeys = [
		"showRecordingPanel",
		"enforceLockdown",
		"enforceMobile",
		"isExtension",
		"isLockdown",
		"isMobile",
		"isChromeOs",
	];

	if (proctoringOption && defaultProctoringOption) {
		const proctoringOptionEntries = Object.entries(proctoringOption).sort();
		const defaultProctoringOptionEntries = Object.entries(defaultProctoringOption).sort();

		for (let i = 0; i < proctoringOptionEntries.length; i++) {
			const [key, value] = proctoringOptionEntries[i];
			const defaultValue = defaultProctoringOptionEntries[i]?.[1];

			if (proctoringOptionFilteredKeys.includes(key)) {
				continue;
			}

			if (key === "applicationBlockList") {
				if (proctoringOption.applicationBlockMode === "off") continue;
			} else if (key === "excludedWebTrafficRecordingUrls") {
				if (!proctoringOption.recordWebTraffic) continue;
			} else if (key === "keyboardShortcutBlockList") {
				if (!proctoringOption.recordInput) continue;
			}

			if (
				((Array.isArray(value) && value.length == 0) || value === null) &&
				((Array.isArray(defaultValue) && defaultValue.length == 0) || defaultValue === null)
			) {
				continue;
			}

			if (value !== defaultValue) {
				return true;
			}
		}
	}
	return false;
}

export function hasExemptedStudentMapChanged(
	initialMap?: Map<number, ExemptedStudent> | null,
	uploadedMap?: Map<number, ExemptedStudent>,
): boolean {
	if (!initialMap || !uploadedMap) return false;

	if (initialMap.size !== uploadedMap.size) {
		return true;
	}
	for (const [key, value] of initialMap) {
		const uploadedValue = uploadedMap?.get(key);
		if (!uploadedValue || JSON.stringify(uploadedValue) !== JSON.stringify(value)) {
			return true;
		}
	}
	return false;
}

export function isThirdPartyStepValid(platforms: ThirdPartyPlatform[], assessmentConfig?: AssessmentConfig) {
	if (!assessmentConfig || !assessmentConfig.thirdPartyAssessmentOption) return true;

	const { password, thirdPartyPlatformId } = assessmentConfig.thirdPartyAssessmentOption;

	const selectedPlatform =
		typeof thirdPartyPlatformId === "number" ? platforms.find((p) => p.platformId === thirdPartyPlatformId) : undefined;

	// Check password validity
	// 1. null is valid
	// 2. string must have length after trimming
	// 3. undefined (or anything else) is invalid
	const isPasswordValid = password === null || (typeof password === "string" && password.trim().length > 0);

	return !!selectedPlatform && isPasswordValid;
}

export function isValidPlatform<T extends Partial<ProctoringOption>>({
	option,
	verityInstituteSettings,
}: {
	option?: Pick<T, "isExtension" | "isLockdown" | "isMobile">;
	verityInstituteSettings?: VerityInstituteSettings;
}) {
	return Boolean(
		option &&
		verityInstituteSettings &&
		((option.isExtension && verityInstituteSettings.allowExtensionPlatform) ||
			(option.isLockdown && verityInstituteSettings.allowLockdownPlatform) ||
			(option.isMobile && verityInstituteSettings.allowMobilePlatform)),
	);
}

export function convertProctoringAssessmentConfiguration(
	config: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">,
	forceThirdParty = false,
	initialPlatformId?: number,
) {
	const thirdPartyAssessmentOption = config.thirdPartyAssessmentOption ?? (forceThirdParty ? {} : undefined);

	// If third party, but no platform -> use platformId passed or try to get platform id from url if there is a url
	if (thirdPartyAssessmentOption && !thirdPartyAssessmentOption.thirdPartyPlatformId) {
		thirdPartyAssessmentOption.thirdPartyPlatformId =
			initialPlatformId || getPlatformIdFromUrl(thirdPartyAssessmentOption.thirdPartyUrl ?? "");
	}

	return {
		...config,
		thirdPartyAssessmentOption,
	};
}

export function convertProctoringOption(option: Partial<ProctoringOption>): Partial<ProctoringOption> {
	return {
		...option,
		hideOtherWindows: option.isLockdown && option.hideOtherWindows,
	};
}

export function getDefaultProctoringOption(verityInstituteSettings: VerityInstituteSettings) {
	return {
		...DEFAULT_PROCTORING_OPTION,
		isExtension: verityInstituteSettings.allowExtensionPlatform,
		isLockdown: verityInstituteSettings.allowExtensionPlatform ? false : verityInstituteSettings.allowLockdownPlatform,
		isMobile:
			verityInstituteSettings.allowExtensionPlatform || verityInstituteSettings.allowLockdownPlatform
				? false
				: verityInstituteSettings.allowMobilePlatform,
	};
}

export function checkIfVerityAssessment(type?: EditQuizType) {
	return type === "verityAssignment" || type === "verityQuiz";
}
